import { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'

/**
 * Hook to persist form data to localStorage
 * Automatically saves form data and restores it on mount
 * 
 * @param {Object} formMethods - React Hook Form methods object
 * @param {string} storageKey - Unique key for localStorage
 * @param {Array} fieldsToSave - Array of field names to save (optional, saves all if not provided)
 * @param {Array} fieldsToExclude - Array of field names to exclude from saving (optional)
 * @returns {Object} - { dataRestored: boolean, clearSavedData: function }
 */
export const useFormPersistence = (formMethods, storageKey, fieldsToSave = null, fieldsToExclude = []) => {
  const [dataRestored, setDataRestored] = useState(false)

  // Watch all form values
  const formValues = useWatch({ control: formMethods.control })

  // Restore data on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = JSON.parse(saved)
        
        // Filter fields if needed
        const dataToRestore = fieldsToSave
          ? Object.keys(parsed).reduce((acc, key) => {
              if (fieldsToSave.includes(key)) {
                acc[key] = parsed[key]
              }
              return acc
            }, {})
          : Object.keys(parsed).reduce((acc, key) => {
              if (!fieldsToExclude.includes(key)) {
                acc[key] = parsed[key]
              }
              return acc
            }, {})

        // Restore form values
        if (Object.keys(dataToRestore).length > 0) {
          Object.keys(dataToRestore).forEach((key) => {
            if (dataToRestore[key] !== null && dataToRestore[key] !== undefined && dataToRestore[key] !== '') {
              formMethods.setValue(key, dataToRestore[key], { shouldDirty: false })
            }
          })
          
          // Show indicator
          setTimeout(() => setDataRestored(true), 100)
          setTimeout(() => setDataRestored(false), 3000)
        }
      }
    } catch (e) {
      console.error('Error restoring form data:', e)
    }
  }, []) // Only run on mount

  // Save data whenever form values change
  useEffect(() => {
    if (!formValues) return

    // Filter data to save
    const dataToSave = fieldsToSave
      ? fieldsToSave.reduce((acc, key) => {
          if (formValues[key] !== undefined && !fieldsToExclude.includes(key)) {
            acc[key] = formValues[key]
          }
          return acc
        }, {})
      : Object.keys(formValues).reduce((acc, key) => {
          if (!fieldsToExclude.includes(key) && formValues[key] !== undefined) {
            acc[key] = formValues[key]
          }
          return acc
        }, {})

    // Only save if there's actual data
    if (Object.keys(dataToSave).length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(dataToSave))
    }
  }, [formValues, storageKey, fieldsToSave, fieldsToExclude])

  // Clear saved data function
  const clearSavedData = () => {
    localStorage.removeItem(storageKey)
  }

  // Prevent page reload on visibility change (mobile lock/unlock)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible - data should already be restored from mount
        console.log('Page visible - form data persisted in localStorage')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return {
    dataRestored,
    clearSavedData,
  }
}

